/**
 * @author Jorge Morayta.
 */

import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { NgProgress } from '@ngx-progressbar/core';
import { DatatableComponent } from '@swimlane/ngx-datatable';
import { ToastrService } from 'ngx-toastr';
import { APIOperableComponent } from '../../../../shared/components/api-operable/api-operable.component';
import { LinkedItemsListManageDialogComponent } from '../../../../shared/components/linked-items-list-manage-dialog/linked-items-list-manage-dialog.component';
import { UIDialogService } from '../../../../shared/services/ui-dialog.service';
import { DataTableUtils } from '../../../../shared/utils/data-table.utils';
import { SortUtils } from '../../../../shared/utils/sort.utils';
import { AuthService } from '../../../security/service/auth.service';
import { ProductUIMessages } from '../../model/product-ui.messages';
import { ProductModel } from '../../model/product.model';
import { ProductService } from '../../service/product.service';

@Component({
  selector: 'line-of-service-view',
  templateUrl:
    './console/app-js/modules/product/components/lineOfService/line-of-service-view.component.html'
})
export class LineOfServiceViewComponent extends APIOperableComponent
  implements OnInit {
  @ViewChild('lineOfServiceDataTable', { static: true })
  lineOfServiceDataTable: DatatableComponent;
  @Input('product') productModel: ProductModel;
  @Input('los') linesOfService: any[];
  @Input('isProductCreateMode') isProductCreateMode: boolean;

  protected originalData: any[];
  private data: any[];
  private filterValue = '';

  constructor(
    toastr: ToastrService,
    progressService: NgProgress,
    authService: AuthService,
    private productService: ProductService,
    private dialogService: UIDialogService
  ) {
    super(progressService, toastr, authService);
  }

  ngOnInit(): void {
    if (this.isProductCreateMode) {
      this.loadForProductCreateMode();
    } else {
      this.loadLineOfService();
    }
  }

  /**
   * Retrieves from endpoint current Lines of Service linked to the product(this.product.id).
   */
  private loadLineOfService(): void {
    this.loadingIndicator = true;
    this.productService.getLOSProductById(this.productModel.id).subscribe(
      (res: any) => {
        let results = res.results.map(los => {
          return {
            id: los.id,
            value: los.name
          };
        });
        this.data = results.sort((a, b) =>
          SortUtils.compareByProperty(a, b, 'value')
        );
        this.originalData = [...this.data];
        this.loadingIndicator = false;
      },
      () => this.onAPIError(ProductUIMessages.LOAD_LINES_OF_SERVICE_ERROR)
    );
    this.isActive();
  }

  /**
   * Retrieves from product model current Lines of Service linked.
   */
  private loadForProductCreateMode(): void {
    if (!this.productModel) {
      this.productModel = new ProductModel();
    }
    this.loadingIndicator = true;
    let results = this.linesOfService.map(los => {
      return {
        id: los.id,
        value: los.name
      };
    });
    this.data = results.sort((a, b) =>
      SortUtils.compareByProperty(a, b, 'value')
    );
    this.originalData = [...this.data];
    this.loadingIndicator = false;
    this.isActive();
  }

  private isActive() {
    this.productModel.isActive =
      this.productModel.isActive === undefined
        ? this.productService.isProductActive(this.productModel.endDate)
        : this.productModel.isActive;
  }

  /**
   * TODO: document this
   */
  private onClearFilter(): void {
    this.filterValue = '';
    this.onFilterData();
  }

  /**
   * Filters out the table results table based on text input element.
   */
  private onFilterData(): void {
    this.data = DataTableUtils.filterDataByProperty(
      this.data,
      this.originalData,
      this.filterValue,
      'value'
    );
  }

  /**
   * Opens dialog popup to manage available lines of service.
   */
  public onManage(): void {
    this.dialogService
      .open(LinkedItemsListManageDialogComponent, [
        {
          provide: Array,
          useValue: this.originalData
        },
        {
          provide: Function,
          useValue: this.productService.getAllLineOfService
        },
        {
          provide: 'allItems',
          useValue: null
        },
        {
          provide: 'title',
          useValue: 'Line of Services'
        },
        {
          provide: 'property',
          useValue: 'name'
        },
        {
          provide: 'idField',
          useValue: 'id'
        }
      ])
      .then(results => {
        if (this.isProductCreateMode) {
          this.doUpdateForCreate(results);
        } else {
          this.doUpdate(results);
        }
      })
      .catch(result => {
        if (result) {
          console.log(result);
        }
        // on popup Cancel button, do nothing by the moment.
      });
  }

  /**
   * TODO: document this
   * @param {Array<any>} results
   */
  private doUpdateForCreate(results: Array<any>): void {
    this.originalData = results.sort((a, b) =>
      SortUtils.compareByProperty(a, b, 'value')
    );
    this.data = [...this.originalData];
    this.onFilterData();
  }

  /**
   * Call the API and  Add/Delete values from the Difference with the original Data
   * @param {Array<any>} results
   */
  private doUpdate(results: Array<any>): void {
    this.progressRef.start();
    let diffNewSelection = results.filter(
      r => !this.originalData.find(l => r.id === l.id)
    );
    let diffDeletedSelection = this.originalData.filter(
      r => !results.find(l => r.id === l.id)
    );

    this.productService
      .updateLineOfServiceForAProduct(
        this.productModel.id,
        diffNewSelection,
        diffDeletedSelection
      )
      .subscribe(
        (res: any) => {
          this.loadLineOfService();
          this.onFilterData();
          this.onAPISuccess(ProductUIMessages.EDIT_LINES_OF_SERVICE_SUCCESS);
        },
        error => {
          console.error(error);
          this.onAPIError(ProductUIMessages.EDIT_LINES_OF_SERVICE_ERROR);
        }
      );
  }

  /**
   * Exposed method to obtain current selected lines of service.
   * @returns {Array<any>}
   */
  public getLinesOfService(): Array<any> {
    return this.originalData;
  }
}
